import React, { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'

// Theme types
export type ThemePreset = 'corporate' | 'soft' | 'vibrant' | 'dark'

export interface CustomColors {
  primary: string
  accent: string
  primaryDark: string
  accentDark: string
}

export interface ThemeConfig {
  preset: ThemePreset
  customColors?: CustomColors
  useCustomColors: boolean
}

interface ThemeContextType {
  theme: ThemeConfig
  setTheme: (theme: ThemeConfig) => void
  applyTheme: (config: ThemeConfig) => void
  resetToPreset: (preset: ThemePreset) => void
  presets: Record<ThemePreset, { name: string; description: string; colors: CustomColors }>
}

const themePresets = {
  corporate: {
    name: 'Rosa Corporativo',
    description: 'Elegante y profesional',
    colors: { primary: '#ec4899', accent: '#eab308', primaryDark: '#be185d', accentDark: '#ca8a04' },
  },
  soft: {
    name: 'Rosa Suave',
    description: 'Delicado y acogedor',
    colors: { primary: '#f472b6', accent: '#fde047', primaryDark: '#db2777', accentDark: '#eab308' },
  },
  vibrant: {
    name: 'Rosa Vibrante',
    description: 'Energético y llamativo',
    colors: { primary: '#db2777', accent: '#facc15', primaryDark: '#9d174d', accentDark: '#ca8a04' },
  },
  dark: {
    name: 'Magenta Intenso',
    description: 'Tonos fuertes y contrastados',
    colors: { primary: '#a21caf', accent: '#f59e0b', primaryDark: '#701a75', accentDark: '#b45309' },
  },
}

const defaultTheme: ThemeConfig = {
  preset: 'corporate',
  useCustomColors: false,
  customColors: themePresets.corporate.colors,
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: ReactNode
}

const hexToRgb = (hex: string) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim())
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : null
}

// Genera una escala 50-950 (triplets "R G B") a partir de un color base (= 500),
// mezclando hacia blanco (claros) o negro (oscuros).
const SHADE_MIX: Record<number, ['white' | 'black' | 'base', number]> = {
  50: ['white', 0.95], 100: ['white', 0.9], 200: ['white', 0.75], 300: ['white', 0.6],
  400: ['white', 0.3], 500: ['base', 0], 600: ['black', 0.15], 700: ['black', 0.3],
  800: ['black', 0.45], 900: ['black', 0.6], 950: ['black', 0.75],
}

const generateScale = (baseHex: string): Record<number, string> => {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return {}
  const mix = (c: number, target: number, t: number) => Math.round(c + (target - c) * t)
  const out: Record<number, string> = {}
  for (const [shade, [dir, t]] of Object.entries(SHADE_MIX)) {
    const tgt = dir === 'white' ? 255 : dir === 'black' ? 0 : -1
    if (dir === 'base') {
      out[Number(shade)] = `${rgb.r} ${rgb.g} ${rgb.b}`
    } else {
      out[Number(shade)] = `${mix(rgb.r, tgt, t)} ${mix(rgb.g, tgt, t)} ${mix(rgb.b, tgt, t)}`
    }
  }
  return out
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeConfig>(defaultTheme)

  useEffect(() => {
    const savedTheme = localStorage.getItem('pavlova-theme')
    if (savedTheme) {
      try {
        const parsed = JSON.parse(savedTheme)
        setThemeState(parsed)
        applyTheme(parsed)
        return
      } catch (e) {
        console.error('Error loading saved theme:', e)
      }
    }
    applyTheme(defaultTheme)
  }, [])

  const applyTheme = (config: ThemeConfig) => {
    const colors = (config.useCustomColors && config.customColors)
      ? config.customColors
      : themePresets[config.preset].colors

    const root = document.documentElement
    const primary = generateScale(colors.primary)
    const accent = generateScale(colors.accent)

    Object.entries(primary).forEach(([shade, value]) => {
      root.style.setProperty(`--color-primary-${shade}`, value)
    })
    Object.entries(accent).forEach(([shade, value]) => {
      root.style.setProperty(`--color-accent-${shade}`, value)
    })
  }

  const setTheme = (newTheme: ThemeConfig) => {
    setThemeState(newTheme)
    applyTheme(newTheme)
    localStorage.setItem('pavlova-theme', JSON.stringify(newTheme))
  }

  const resetToPreset = (preset: ThemePreset) => {
    setTheme({ preset, useCustomColors: false, customColors: themePresets[preset].colors })
  }

  const value: ThemeContextType = {
    theme,
    setTheme,
    applyTheme,
    resetToPreset,
    presets: themePresets,
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}
